import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { forkJoin } from 'rxjs';
import { UserOfficeDto } from '../../../models/user.models';
import { AuthService } from '../../../services/auth.service';
import { ResourceService } from '../../../services/resource.service';
import { OfficeService } from '../../../services/office.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { describeApiError } from '../../../utils/api-error.utils';

export interface UserOfficesDialogData {
  employeeId: string;
  userName: string;
  /**
   * The user's offices (primary + secondary) as already loaded by the User Management
   * directory endpoint (admin-gated, not practice-area scoped). The primary office is taken
   * from here so the dialog never calls the practice-area-restricted resource-detail endpoint.
   */
  offices?: UserOfficeDto[];
}

interface OfficeOption {
  code: string;
  description: string;
  active: boolean;
  /** Office state/letter code (e.g. "NY"). */
  stateCode: string | null;
  /** Office country code (e.g. "US"). */
  country: string | null;
}

interface AssignedOffice {
  officeCode: string;
  officeName: string;
  isPrimary: boolean;
  /** Secondary-office association id; needed to delete. Null for the primary office. */
  associationId: number | null;
  stateCode: string | null;
  country: string | null;
}

/** A single row in the merged list. `primary`/`secondary` are assigned (primary
 *  is locked); `available` can be added. */
type RowKind = 'primary' | 'secondary' | 'available';

interface OfficeRow {
  code: string;
  name: string;
  kind: RowKind;
  /** Secondary-office association id; only set for secondary rows. */
  associationId: number | null;
  /** Display meta line: state + country code (e.g. "NY, US"). */
  meta: string;
}

@Component({
  selector: 'app-user-offices-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: './user-offices-dialog.component.html',
  styleUrls: ['./user-offices-dialog.component.scss'],
})
export class UserOfficesDialogComponent implements OnInit {
  private dialogRef =
    inject<MatDialogRef<UserOfficesDialogComponent, boolean>>(MatDialogRef);
  data = inject<UserOfficesDialogData>(MAT_DIALOG_DATA);
  private resourceService = inject(ResourceService);
  private secondaryOfficeService = inject(OfficeService);
  private snackbarService = inject(SnackbarService);
  private authService = inject(AuthService);

  isLoading = false;
  /** All active offices in the system, used as the add-search source. */
  private allOffices: OfficeOption[] = [];
  /** The user's current offices (primary + secondaries), kept in sync. */
  assigned: AssignedOffice[] = [];

  searchQuery = '';
  /**
   * The rendered, position-stable rows. Re-sorted (assigned-first) only on load
   * and on search change; adding/removing mutates a row's kind in place so it
   * holds its position in the list until the filter changes or the dialog reopens.
   */
  displayRows: OfficeRow[] = [];
  /** Office codes with an in-flight add/remove request. */
  loadingCodes = new Set<string>();
  private changed = false;

  get canManage(): boolean {
    return this.authService.canManageSecondaryOffices();
  }

  /** The primary office name shown in the summary band (or "None" if unset). */
  get primaryName(): string {
    return this.assigned.find((a) => a.isPrimary)?.officeName ?? 'None';
  }

  /** Count of secondary (non-primary) offices, shown beneath the primary. */
  get additionalCount(): number {
    return this.assigned.filter((a) => !a.isPrimary).length;
  }

  ngOnInit(): void {
    this.isLoading = true;
    forkJoin({
      offices: this.resourceService.getUniqueOffices(),
      secondary: this.secondaryOfficeService.getByEmployeeId(
        this.data.employeeId,
      ),
    }).subscribe({
      next: ({ offices, secondary }) => {
        this.allOffices = offices.map((o) => ({
          code: o.code,
          description: o.description,
          active: o.active,
          stateCode: o.letterCode,
          country: o.country,
        }));

        const officeByCode = new Map(
          this.allOffices.map((o) => [o.code, o]),
        );

        const assigned: AssignedOffice[] = [];

        // Primary office: taken from the directory-provided offices (admin-gated, not
        // practice-area scoped). This avoids the resource-detail endpoint, which restricts
        // by practice area and would block managing offices for resources outside the
        // current user's practice areas.
        const primaryOffice = (this.data.offices ?? []).find((o) => o.isPrimary);
        if (primaryOffice?.officeCode) {
          const office = officeByCode.get(primaryOffice.officeCode);
          assigned.push({
            officeCode: primaryOffice.officeCode,
            officeName: office?.description || primaryOffice.officeName,
            isPrimary: true,
            associationId: null,
            stateCode: office?.stateCode ?? null,
            country: office?.country ?? null,
          });
        }

        // Secondary offices.
        secondary.forEach((s) => {
          const office = officeByCode.get(s.officeCode);
          assigned.push({
            officeCode: s.officeCode,
            officeName: office?.description || s.officeCode,
            isPrimary: false,
            associationId: s.id,
            stateCode: office?.stateCode ?? null,
            country: office?.country ?? null,
          });
        });

        this.assigned = assigned;
        this.rebuildRows();
        this.isLoading = false;
      },
      error: () => {
        this.allOffices = [];
        this.assigned = [];
        this.isLoading = false;
        this.snackbarService.showError('Failed to load offices');
      },
    });
  }

  isCodeLoading(code: string): boolean {
    return this.loadingCodes.has(code);
  }

  /**
   * Rebuild the merged, search-filtered list (assigned first — primary, then
   * secondary — followed by available). Called only on load and on search
   * change, so add/remove can hold a row's position between rebuilds.
   */
  private rebuildRows(): void {
    const query = this.searchQuery.trim().toLowerCase();
    const rows: OfficeRow[] = this.assigned
      .filter((a) => !a.isPrimary)
      .map((a) => ({
        code: a.officeCode,
        name: a.officeName,
        kind: 'secondary' as const,
        associationId: a.associationId,
        meta: this.formatLocation(a.stateCode, a.country),
      }));

    if (this.canManage) {
      const assignedCodes = new Set(
        this.assigned.map((a) => a.officeCode).filter((c) => !!c),
      );
      this.allOffices
        .filter((o) => o.active && !assignedCodes.has(o.code))
        .forEach((o) =>
          rows.push({
            code: o.code,
            name: o.description,
            kind: 'available',
            associationId: null,
            meta: this.formatLocation(o.stateCode, o.country),
          }),
        );
    }

    const weight: Record<RowKind, number> = {
      primary: 0,
      secondary: 1,
      available: 2,
    };
    this.displayRows = rows
      .filter(
        (r) =>
          !query ||
          r.name.toLowerCase().includes(query) ||
          r.code.toLowerCase().includes(query),
      )
      .sort(
        (a, b) =>
          weight[a.kind] - weight[b.kind] || a.name.localeCompare(b.name),
      );
  }

  /** Build the row meta line from the office's state and country codes (e.g. "NY, US"). */
  private formatLocation(
    stateCode: string | null,
    country: string | null,
  ): string {
    return [stateCode, country]
      .map((v) => v?.trim())
      .filter((v): v is string => !!v)
      .join(', ');
  }

  onSearchChange(): void {
    this.rebuildRows();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.rebuildRows();
  }

  /** Update a rendered row's kind (and association id) in place, keeping its position. */
  private setRowState(
    code: string,
    kind: RowKind,
    associationId: number | null,
  ): void {
    this.displayRows = this.displayRows.map((r) =>
      r.code === code ? { ...r, kind, associationId } : r,
    );
  }

  onRowClick(row: OfficeRow): void {
    if (!this.canManage || this.isCodeLoading(row.code)) return;
    if (row.kind === 'available') {
      this.add(row.code, row.name);
    } else if (row.kind === 'secondary' && row.associationId !== null) {
      this.remove(row.associationId, row.code, row.name);
    }
  }

  private add(code: string, name: string): void {
    this.loadingCodes.add(code);
    const office = this.allOffices.find((o) => o.code === code);
    this.secondaryOfficeService.create(this.data.employeeId, code).subscribe({
      next: (created) => {
        this.assigned = [
          ...this.assigned,
          {
            officeCode: code,
            officeName: name,
            isPrimary: false,
            associationId: created.id,
            stateCode: office?.stateCode ?? null,
            country: office?.country ?? null,
          },
        ];
        this.setRowState(code, 'secondary', created.id);
        this.loadingCodes.delete(code);
        this.changed = true;
        this.snackbarService.showSuccess(
          `Added ${name} to ${this.data.userName}`,
        );
      },
      error: (err) => {
        this.loadingCodes.delete(code);
        this.snackbarService.showError(describeApiError(err, 'Failed to add office'));
      },
    });
  }

  private remove(associationId: number, code: string, name: string): void {
    this.loadingCodes.add(code);
    this.secondaryOfficeService.delete(associationId).subscribe({
      next: () => {
        this.assigned = this.assigned.filter(
          (a) => a.associationId !== associationId,
        );
        this.setRowState(code, 'available', null);
        this.loadingCodes.delete(code);
        this.changed = true;
        this.snackbarService.showSuccess(
          `Removed ${name} from ${this.data.userName}`,
        );
      },
      error: (err) => {
        this.loadingCodes.delete(code);
        this.snackbarService.showError(
          describeApiError(err, 'Failed to remove office'),
        );
      },
    });
  }

  close(): void {
    this.dialogRef.close(this.changed);
  }
}
